import React, { Component, Fragment } from 'react';
import { Layout, Typography, Card, Row, Col, Table, Input, Button, Badge, Space, Popconfirm, Select, DatePicker, message, Spin, Tag } from 'antd';
import { SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import AdminPortalHeader from '../../components/common/admin_portal_header';
import { getAllCorporateDiscounts, deleteCorporateDiscount, toggleCorporateDiscount } from '../../api/pets-api';
import moment from 'moment';

const { Paragraph, Title } = Typography;
const { Content } = Layout;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

class AdminCorporateDiscounts extends Component {
    constructor(props) {
        super(props);
        this.state = {
            discounts: [],
            loading: false,
            pagination: {
                current: 1,
                pageSize: 10,
                total: 0
            },
            filters: {
                status: 'all',
                search: ''
            }
        };
    }

    componentDidMount() {
        this.fetchDiscounts();
    }

    fetchDiscounts = async (page = 1, pageSize = 10) => {
        this.setState({ loading: true });
        try {
            const { filters } = this.state;
            const params = {
                page,
                pageSize,
                status: filters.status,
                search: filters.search
            };

            const response = await getAllCorporateDiscounts(params);
            
            if (response.status === 200 && response.data.status === 'ok') {
                const { discounts, pagination } = response.data.data;
                
                this.setState({
                    discounts: discounts || [],
                    pagination: {
                        current: pagination?.currentPage || page,
                        pageSize: pagination?.pageSize || pageSize,
                        total: pagination?.totalItems || 0
                    },
                    loading: false
                });
            } else {
                message.error('Failed to load corporate discounts');
                this.setState({ loading: false });
            }
        } catch (error) {
            console.error('Error fetching discounts:', error);
            message.error('Error loading corporate discounts');
            this.setState({ loading: false });
        }
    };

    handleTableChange = (pagination) => {
        this.fetchDiscounts(pagination.current, pagination.pageSize);
    };

    handleSearch = (value) => {
        this.setState(
            prevState => ({
                filters: { ...prevState.filters, search: value }
            }),
            () => this.fetchDiscounts()
        );
    };

    handleStatusFilter = (value) => {
        this.setState(
            prevState => ({
                filters: { ...prevState.filters, status: value }
            }),
            () => this.fetchDiscounts()
        );
    };

    handleToggleStatus = async (id, currentStatus) => {
        try {
            const response = await toggleCorporateDiscount(id);
            if (response.status === 200 && response.data.status === 'ok') {
                message.success(response.data.message);
                this.fetchDiscounts(this.state.pagination.current);
            } else {
                message.error('Failed to toggle status');
            }
        } catch (error) {
            console.error('Error toggling status:', error);
            message.error('Error toggling discount status');
        }
    };

    handleDelete = async (id) => {
        try {
            const response = await deleteCorporateDiscount(id);
            if (response.status === 200 && response.data.status === 'ok') {
                message.success('Corporate discount deleted successfully');
                this.fetchDiscounts(this.state.pagination.current);
            } else {
                message.error('Failed to delete discount');
            }
        } catch (error) {
            console.error('Error deleting discount:', error);
            message.error('Error deleting corporate discount');
        }
    };

    navigateToCreate = () => {
        this.props.history.push('/admin_corporate_discounts/create');
    };

    navigateToDetails = (id) => {
        this.props.history.push(`/admin_corporate_discounts/${id}`);
    };

    render() {
        const { discounts, loading, pagination } = this.state;

        const columns = [
            {
                title: 'Company Name',
                dataIndex: 'companyName',
                key: 'companyName',
                render: (text, record) => (
                    <Fragment>
                        <Paragraph className="mb-0 font-weight-bold">{text}</Paragraph>
                        <Paragraph className="text-muted mb-0" style={{ fontSize: '12px' }}>
                            {record.emailPattern}
                        </Paragraph>
                    </Fragment>
                )
            },
            {
                title: 'Discount',
                dataIndex: 'discountPercentage',
                key: 'discountPercentage',
                width: 100,
                render: (percentage) => (
                    <Tag color="green" style={{ fontSize: '14px', fontWeight: 'bold' }}>
                        {percentage}%
                    </Tag>
                )
            },
            {
                title: 'Status',
                dataIndex: 'isActive',
                key: 'isActive',
                width: 100,
                render: (isActive) => (
                    <Badge 
                        status={isActive ? "success" : "default"} 
                        text={isActive ? "Active" : "Inactive"} 
                    />
                )
            },
            {
                title: 'Duration',
                key: 'duration',
                render: (_, record) => (
                    <Fragment>
                        <Paragraph className="mb-0" style={{ fontSize: '12px' }}>
                            <strong>Start:</strong> {moment(record.startDate).format('DD MMM YYYY')}
                        </Paragraph>
                        {record.endDate && (
                            <Paragraph className="mb-0" style={{ fontSize: '12px' }}>
                                <strong>End:</strong> {moment(record.endDate).format('DD MMM YYYY')}
                            </Paragraph>
                        )}
                    </Fragment>
                )
            },
            {
                title: 'Usage',
                key: 'usage',
                width: 120,
                render: (_, record) => (
                    <Fragment>
                        <Paragraph className="mb-0">
                            {record.currentUsageCount || 0} 
                            {record.maxUsageCount ? ` / ${record.maxUsageCount}` : ' / ∞'}
                        </Paragraph>
                    </Fragment>
                )
            },
            {
                title: 'Contact',
                key: 'contact',
                render: (_, record) => (
                    record.contactPerson ? (
                        <Fragment>
                            <Paragraph className="mb-0" style={{ fontSize: '12px' }}>
                                {record.contactPerson}
                            </Paragraph>
                            {record.contactEmail && (
                                <Paragraph className="text-muted mb-0" style={{ fontSize: '11px' }}>
                                    {record.contactEmail}
                                </Paragraph>
                            )}
                        </Fragment>
                    ) : (
                        <span className="text-muted">-</span>
                    )
                )
            },
            {
                title: 'Actions',
                key: 'actions',
                render: (_, record) => (
                    <Space size="small" wrap>
                        <Button
                            type="link"
                            icon={<EyeOutlined />}
                            onClick={() => this.navigateToDetails(record.id)}
                            title="View Details"
                        />
                        <Button
                            type="link"
                            icon={<EditOutlined />}
                            onClick={() => this.navigateToDetails(record.id)}
                            title="Edit"
                        />
                        <Popconfirm
                            title={`Are you sure you want to ${record.isActive ? 'deactivate' : 'activate'} this discount?`}
                            onConfirm={() => this.handleToggleStatus(record.id, record.isActive)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                type="link"
                                style={{ color: record.isActive ? 'orange' : 'green' }}
                                title={record.isActive ? 'Deactivate' : 'Activate'}
                            >
                                {record.isActive ? 'Deactivate' : 'Activate'}
                            </Button>
                        </Popconfirm>
                        <Popconfirm
                            title="Are you sure you want to delete this discount?"
                            onConfirm={() => this.handleDelete(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                type="link"
                                danger
                                icon={<DeleteOutlined />}
                                title="Delete"
                            />
                        </Popconfirm>
                    </Space>
                )
            }
        ];

        return (
            <Fragment>
                <Layout className="main-layout dashboard-layout">
                    <AdminPortalHeader />
                    <Content className="dashboard-content">
                        <div className="container-fluid">
                            <Row gutter={[15, 15]} className="mb-4">
                                <Col xs={24}>
                                    <Card>
                                        <Row gutter={[15, 15]} align="middle">
                                            <Col xs={24} sm={12} md={8}>
                                                <Title level={3} className="mb-0">Corporate Discounts</Title>
                                            </Col>
                                            <Col xs={24} sm={12} md={16} style={{ textAlign: 'right' }}>
                                                <Button
                                                    type="primary"
                                                    icon={<PlusOutlined />}
                                                    onClick={this.navigateToCreate}
                                                    size="large"
                                                >
                                                    Add New Discount
                                                </Button>
                                            </Col>
                                        </Row>
                                    </Card>
                                </Col>
                            </Row>

                            <Row gutter={[15, 15]} className="mb-4">
                                <Col xs={24}>
                                    <Card>
                                        <Row gutter={[15, 15]}>
                                            <Col xs={24} sm={12} md={8}>
                                                <Search
                                                    placeholder="Search by company name or email pattern"
                                                    allowClear
                                                    enterButton={<SearchOutlined />}
                                                    size="large"
                                                    onSearch={this.handleSearch}
                                                />
                                            </Col>
                                            <Col xs={24} sm={12} md={4}>
                                                <Select
                                                    placeholder="Filter by status"
                                                    size="large"
                                                    style={{ width: '100%' }}
                                                    defaultValue="all"
                                                    onChange={this.handleStatusFilter}
                                                >
                                                    <Option value="all">All Status</Option>
                                                    <Option value="active">Active Only</Option>
                                                    <Option value="inactive">Inactive Only</Option>
                                                </Select>
                                            </Col>
                                        </Row>
                                    </Card>
                                </Col>
                            </Row>

                            <Row gutter={[15, 15]}>
                                <Col xs={24}>
                                    <Card>
                                        <Table
                                            columns={columns}
                                            dataSource={discounts}
                                            loading={loading}
                                            rowKey="id"
                                            pagination={pagination}
                                            onChange={this.handleTableChange}
                                            scroll={{ x: 1200 }}
                                        />
                                    </Card>
                                </Col>
                            </Row>
                        </div>
                    </Content>
                </Layout>
            </Fragment>
        );
    }
}

export default AdminCorporateDiscounts;
